import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { Pair, TopOfBookSource, TopOfBookSourceRequest, TopOfBookSourceUpdateRequest } from '@api/core';
import { TopOfBookSourceEnum } from '@shared/enums/top-of-book-source';
import { asOptional, isDefined } from '@stillmandigital/common';
import { combineLatest, map, startWith } from 'rxjs';

import { MatCheckbox } from '@angular/material/checkbox';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { LetDirective } from '@ngrx/component';
import { SourceSelectorComponent } from '@shared-modules/settings/components/source-selector/source-selector.component';
import { PairSelectorComponent } from '@shared/components/pair-selector/pair-selector.component';
import { TopOfBookSourceViewModel, typesWithSources } from '@shared/store/features/shared-settings/top-of-book.feature';
import { SharedStore } from '@shared/store/shared.signal-store';
import { getValueByType } from './update-top-of-book-source-dialog.utils';

@Component({
  selector: 'app-update-top-of-book-source-dialog',
  templateUrl: './update-top-of-book-source-dialog.component.html',
  styleUrl: './update-top-of-book-source-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogTitle,
    MatIcon,
    MatIconButton,
    MatCheckbox,
    ReactiveFormsModule,
    MatDialogContent,
    MatFormField,
    MatSelect,
    MatOption,
    MatInput,
    MatLabel,
    PairSelectorComponent,
    SourceSelectorComponent,
    LetDirective,
  ],
})
export class UpdateTopOfBookSourceDialogComponent implements OnInit {
  public readonly data = inject<TopOfBookSourceViewModel>(MAT_DIALOG_DATA);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _fb = inject(NonNullableFormBuilder);
  private readonly _sharedStore = inject(SharedStore);

  public readonly mode = signal<'Create' | 'Update'>(isDefined(this.data.id) ? 'Update' : 'Create');
  public readonly sourceTypes = computed(() =>
    TopOfBookSourceEnum.getTranslatedOrderedValues().filter(type => !type.deprecated || this.mode() === 'Update'),
  );
  public readonly sources$ = toObservable(this._sharedStore.topOfBookSources);

  public readonly lps = this._sharedStore.liquidityProviders;

  public readonly basePairs = this._sharedStore.basePairs;

  public readonly form = this._fb.group({
    type: [asOptional<TopOfBookSource.TypeEnum>(undefined), Validators.required],
    name: [asOptional<string>(), Validators.required],
    pair: [asOptional<Pair>(), Validators.required],
    enabled: false,
    source1: asOptional<TopOfBookSource>(),
    weight1: asOptional<number>(),
    source2: asOptional<TopOfBookSource>(),
    weight2: asOptional<number>(),
    source3: asOptional<TopOfBookSource>(),
    weight3: asOptional<number>(),
    source4: asOptional<TopOfBookSource>(),
    weight4: asOptional<number>(),
    lpId: asOptional<number>(),
  });

  public readonly lpPairs$ = combineLatest([
    toObservable(this._sharedStore.lpPairs),
    this.form.controls.lpId.valueChanges.pipe(startWith(this.data.lpId)),
  ]).pipe(map(([pairs, lpId]) => pairs.filter(p => p.liquidityProviderId === lpId)));

  public readonly type$ = this.form.controls.type.valueChanges.pipe(startWith(this.data.type));

  public readonly isAggregateType$ = this.type$.pipe(map(type => type === TopOfBookSource.TypeEnum.Aggregate));

  public readonly isSourceType$ = this.type$.pipe(map(type => !!type && typesWithSources.includes(type)));

  public readonly firstSourceName$ = this.type$.pipe(
    map(type => {
      switch (type) {
        case TopOfBookSource.TypeEnum.Multiply:
          return 'Multiplier';
        case TopOfBookSource.TypeEnum.Divide:
          return 'Dividend';
        case TopOfBookSource.TypeEnum.PerpSpotChaining:
          return 'Perpetual';
        default:
          return 'Source 1';
      }
    }),
  );

  public readonly secondSourceName$ = this.type$.pipe(
    map(type => {
      switch (type) {
        case TopOfBookSource.TypeEnum.Multiply:
          return 'Multiplicand';
        case TopOfBookSource.TypeEnum.Divide:
          return 'Divisor';
        case TopOfBookSource.TypeEnum.PerpSpotChaining:
          return 'Spot';
        default:
          return 'Source 2';
      }
    }),
  );

  public readonly isLpType$ = this.type$.pipe(map(type => type === TopOfBookSource.TypeEnum.LiquidityProvider));

  constructor() {
    this.form.reset(this.data);
  }

  public ngOnInit(): void {
    if (this.mode() === 'Update') {
      this.form.controls.type.disable();
    }

    this.type$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(type => {
      this._updateLpPairControlValidators(type);
      this._updateSourceControlsValidators(type);
    });

    this.form.controls.lpId.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this.form.controls.pair.reset());
  }

  public update() {
    const update = getValueByType(this.form.getRawValue());

    if (this.mode() === 'Create') {
      const request = update as TopOfBookSourceRequest;
      this._sharedStore.createTopOfBookSource(request);
    } else {
      const request = {
        id: this.data.id,
        ...update,
      } as TopOfBookSourceUpdateRequest;
      this._sharedStore.updateTopOfBookSource(request);
    }
  }

  private _updateLpPairControlValidators(type?: TopOfBookSource.TypeEnum) {
    if (type === TopOfBookSource.TypeEnum.LiquidityProvider) {
      this.form.controls.lpId.setValidators([Validators.required]);
      this.form.controls.lpId.updateValueAndValidity();
    } else {
      this.form.controls.lpId.clearValidators();
      this.form.controls.lpId.updateValueAndValidity();
    }
  }

  private _updateSourceControlsValidators(type?: TopOfBookSource.TypeEnum) {
    const sourceControls = [this.form.controls.source1, this.form.controls.source2];
    const aggregateControls = [this.form.controls.weight1, this.form.controls.weight2];

    if (type && typesWithSources.includes(type)) {
      sourceControls.forEach(control => {
        control.setValidators([Validators.required]);
        control.updateValueAndValidity();
      });

      if (type === TopOfBookSource.TypeEnum.Aggregate) {
        aggregateControls.forEach(control => {
          control.setValidators([Validators.required]);
          control.updateValueAndValidity();
        });
      } else {
        aggregateControls.forEach(control => {
          control.clearValidators();
          control.updateValueAndValidity();
        });
      }
    } else {
      [...sourceControls, ...aggregateControls].forEach(control => {
        control.clearValidators();
        control.updateValueAndValidity();
      });
    }
  }
}
